import { useEffect } from 'react'

const BASE_URL = 'https://ratygo.pl'
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`

interface SEOHeadProps {
  title: string
  description: string
  slug?: string
  keywords?: string[]
  ogImage?: string
  jsonLd?: Record<string, unknown>
}

/**
 * Sets metadata tags via direct DOM manipulation in useEffect.
 *
 * Why not React 19's native metadata or react-helmet-async?
 * - Both create new tags on hydration without recognizing prerendered ones
 * - This causes duplicates when using Puppeteer-based prerendering
 *
 * This implementation is idempotent — it queries existing tags by their
 * unique attribute (name/property/rel) and UPDATES them, never appends.
 * Works correctly with prerender (Puppeteer runs the effect, captures DOM)
 * and hydration (effect re-runs, finds existing tags, updates in place).
 */
export function SEOHead({ title, description, slug = '', keywords, ogImage, jsonLd }: SEOHeadProps) {
  const canonical = `${BASE_URL}${slug}`
  const fullTitle = slug === '/' || slug === '' || title.includes('RatyGo') ? title : `${title} | RatyGo`
  const image = ogImage || DEFAULT_OG_IMAGE
  const ogType = slug.startsWith('/blog/') ? 'article' : 'website'

  useEffect(() => {
    document.title = fullTitle

    setMeta('name', 'description', description)
    setMeta('name', 'keywords', keywords?.join(', '))
    setMeta('property', 'og:title', fullTitle)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', canonical)
    setMeta('property', 'og:type', ogType)
    setMeta('property', 'og:image', image)
    setMeta('property', 'og:image:width', '1200')
    setMeta('property', 'og:image:height', '630')
    setMeta('name', 'twitter:title', fullTitle)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'twitter:image', image)
    setLink('canonical', canonical)
    setJsonLd(jsonLd)
  }, [fullTitle, description, canonical, ogType, image, keywords, jsonLd])

  return null
}

function setMeta(attr: 'name' | 'property', key: string, value: string | undefined) {
  if (!value) {
    document.querySelector(`meta[${attr}="${key}"]`)?.remove()
    return
  }
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  if (el.getAttribute('content') !== value) {
    el.setAttribute('content', value)
  }
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  if (el.getAttribute('href') !== href) {
    el.setAttribute('href', href)
  }
}

function setJsonLd(data: Record<string, unknown> | undefined) {
  const SELECTOR = 'script[type="application/ld+json"][data-seo="true"]'
  let el = document.querySelector<HTMLScriptElement>(SELECTOR)
  if (!data) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement('script')
    el.setAttribute('type', 'application/ld+json')
    el.setAttribute('data-seo', 'true')
    document.head.appendChild(el)
  }
  const json = JSON.stringify(data)
  if (el.textContent !== json) {
    el.textContent = json
  }
}
